import { useEffect, useState } from 'react';

import type { ExperienceInitialFormType } from './useExperienceData';
import type {
  FormValidationErrors,
  ValidationErrorResult,
} from './validation/openingConfigurationValidation';
import { validateOpeningConfiguration } from './validation/openingConfigurationValidation';

export type {
  ValidationError,
  StockValidationError,
  PriceValidationError,
  ValidationErrorResult,
  FormValidationErrors,
} from './validation/openingConfigurationValidation';

type Subscriber = (errors: FormValidationErrors) => void;

const subscribers = new Set<Subscriber>();

const createEmptyErrors = (): FormValidationErrors => ({
  stockError: { isValid: true, errors: [] },
  priceError: { isValid: true, errors: [] },
  guestsError: { isValid: true, errors: [] },
});

const validationStore = {
  errors: createEmptyErrors(),
};

const notifySubscribers = (): void => {
  subscribers.forEach((subscriber) => subscriber(validationStore.errors));
};

export const setValidationErrors = (errors: FormValidationErrors): void => {
  validationStore.errors = errors;
  notifySubscribers();
};

export const setStockValidationError = (error: ValidationErrorResult): void => {
  validationStore.errors = { ...validationStore.errors, stockError: error };
  notifySubscribers();
};

export const resetValidationErrors = (): void => {
  validationStore.errors = createEmptyErrors();
  notifySubscribers();
};

export const useValidationErrors = (): FormValidationErrors => {
  const [errors, setErrors] = useState<FormValidationErrors>(
    validationStore.errors
  );

  useEffect(() => {
    subscribers.add(setErrors);

    return () => {
      subscribers.delete(setErrors);
    };
  }, []);

  return errors;
};

export const validateAllConfigurations = (
  formValues: ExperienceInitialFormType
): FormValidationErrors => validateOpeningConfiguration(formValues);

// ============================================================================
// Form dirty state (for programmatic form changes that bypass onFieldsChange)
// ============================================================================

let formDirtyFlag = false;
let onFormDirtyCallback: (() => void) | null = null;

export const registerFormDirtyCallback = (callback: () => void): void => {
  onFormDirtyCallback = callback;
};

export const unregisterFormDirtyCallback = (): void => {
  onFormDirtyCallback = null;
};

export const markFormDirty = (): void => {
  formDirtyFlag = true;

  if (onFormDirtyCallback) {
    onFormDirtyCallback();
  }
};

export const isFormDirty = (): boolean => formDirtyFlag;

export const resetFormDirty = (): void => {
  formDirtyFlag = false;
};
